import type { AnalyticsEvent, AnalyticsProps, AnalyticsValue, ErrorCode } from "./net/protocol";
import type { Session } from "./net/session";
import { ConnectionError, ServerError, type GameSocket } from "./net/socket";

const NAME = /^[a-z][a-z0-9_]{0,63}$/;
const RESERVED_NAMES: readonly string[] = ["session_start", "session_end"];
const MAX_PROPS = 32;
const MAX_STRING = 256;
const MAX_REQUEST_EVENTS = 50;
/** Retrying these can't succeed, so the batch is dropped. */
const DROP_CODES: readonly ErrorCode[] = ["bad_request", "unknown_type"];

export interface Timers {
  set(callback: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

const globalTimers: Timers = {
  set: (callback, ms) => setTimeout(callback, ms),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface AnalyticsOptions {
  /** Unix ms. */
  now?: () => number;
  timers?: Timers;
  newId?: () => string;
  /** Dev diagnostics for dropped events and props; silent in production builds by default. */
  warn?: (message: string) => void;
  /** Queued events wait at most this long. */
  flushIntervalMs?: number;
  /** Sends right away once this many events are queued. */
  batchSize?: number;
  /** The oldest events are dropped beyond this. */
  maxQueue?: number;
}

interface Queued {
  event: AnalyticsEvent;
  retried: boolean;
}

/**
 * Batches events and sends them as analytics.track while the session is ready. Fire-and-forget:
 * track() never throws, and a failed batch is resent at most once.
 */
export class Analytics {
  private queue: Queued[] = [];
  private inFlight = false;
  private timer: unknown;
  private sendLimit = MAX_REQUEST_EVENTS;
  private readonly now: () => number;
  private readonly timers: Timers;
  private readonly newId: () => string;
  private readonly warn: (message: string) => void;
  private readonly flushIntervalMs: number;
  private readonly batchSize: number;
  private readonly maxQueue: number;

  constructor(
    private readonly socket: Pick<GameSocket, "request">,
    private readonly session: Pick<Session, "state" | "on">,
    options: AnalyticsOptions = {},
  ) {
    this.now = options.now ?? Date.now;
    this.timers = options.timers ?? globalTimers;
    this.newId = options.newId ?? randomId;
    this.warn = options.warn ?? (import.meta.env.DEV ? (message) => console.warn(message) : () => {});
    this.flushIntervalMs = options.flushIntervalMs ?? 5000;
    this.batchSize = Math.min(options.batchSize ?? 20, MAX_REQUEST_EVENTS);
    this.maxQueue = options.maxQueue ?? 500;
    session.on("state", (state) => {
      if (state.kind === "ready") this.flush();
      else this.clearTimer();
    });
  }

  /** Events waiting to be sent, not counting a request in flight. */
  get queued(): number {
    return this.queue.length;
  }

  track(name: string, props?: Record<string, unknown>): void {
    try {
      if (typeof name !== "string" || !NAME.test(name) || RESERVED_NAMES.includes(name)) {
        this.warn(`analytics: dropped event with invalid name ${String(name)}`);
        return;
      }
      const event: AnalyticsEvent = { id: this.newId(), name, ts: Math.max(0, Math.round(this.now())) };
      const clean = props === undefined ? undefined : this.normalise(name, props);
      if (clean) event.props = clean;
      this.queue.push({ event, retried: false });
      this.trim();
      this.schedule();
    } catch (err) {
      console.error("analytics: track failed", err);
    }
  }

  /** Sends queued events now, e.g. before the app goes to the background. */
  flush(): void {
    if (this.queue.length === 0 || !this.ready) {
      this.clearTimer();
      return;
    }
    if (this.inFlight) return;
    this.clearTimer();
    const batch = this.queue.splice(0, this.sendLimit);
    this.inFlight = true;
    let sent: Promise<unknown>;
    try {
      sent = this.socket.request("analytics.track", { events: batch.map((queued) => queued.event) });
    } catch (err) {
      sent = Promise.reject(err);
    }
    sent.then(
      () => {
        this.sendLimit = MAX_REQUEST_EVENTS;
        this.inFlight = false;
        this.schedule();
      },
      (err: unknown) => {
        this.onFailure(batch, err);
        this.inFlight = false;
        this.arm();
      },
    );
  }

  /** Call from onActiveChange before the session pauses, so the last events still go out. */
  setActive(active: boolean): void {
    if (!active) this.flush();
  }

  private get ready(): boolean {
    return this.session.state.kind === "ready";
  }

  private onFailure(batch: Queued[], err: unknown): void {
    if (err instanceof ServerError && DROP_CODES.includes(err.code)) {
      this.warn(`analytics: batch of ${batch.length} rejected: ${err.message}`);
      return;
    }
    if (err instanceof ConnectionError && err.reason === "too_large") {
      if (batch.length === 1) {
        this.warn(`analytics: dropped event ${batch[0]?.event.name}: too large`);
        return;
      }
      // Never sent: resend in smaller requests.
      this.sendLimit = Math.ceil(batch.length / 2);
      this.requeue(batch);
      return;
    }
    if (err instanceof ConnectionError && err.reason === "not_open") {
      this.requeue(batch);
      return;
    }
    // Maybe delivered: the server deduplicates by id, so resend once.
    this.requeue(batch.filter((queued) => !queued.retried).map((queued) => ({ event: queued.event, retried: true })));
  }

  private requeue(batch: Queued[]): void {
    this.queue.unshift(...batch);
    this.trim();
  }

  private trim(): void {
    if (this.queue.length > this.maxQueue) this.queue.splice(0, this.queue.length - this.maxQueue);
  }

  private schedule(): void {
    if (this.queue.length >= this.batchSize) this.flush();
    else this.arm();
  }

  /** Starts the flush timer only while there is something to send and a session to send it on. */
  private arm(): void {
    if (this.timer !== undefined || this.queue.length === 0 || !this.ready) return;
    this.timer = this.timers.set(() => {
      this.timer = undefined;
      this.flush();
    }, this.flushIntervalMs);
  }

  private clearTimer(): void {
    if (this.timer === undefined) return;
    this.timers.clear(this.timer);
    this.timer = undefined;
  }

  private normalise(name: string, props: unknown): AnalyticsProps | undefined {
    if (typeof props !== "object" || props === null || Array.isArray(props)) {
      this.warn(`analytics: ${name}: props must be an object`);
      return undefined;
    }
    const out: AnalyticsProps = {};
    const dropped: string[] = [];
    let count = 0;
    for (const [key, raw] of Object.entries(props)) {
      if (raw === undefined) continue;
      const value = propValue(raw);
      if (!NAME.test(key) || value === undefined || count === MAX_PROPS) {
        dropped.push(key);
        continue;
      }
      out[key] = value;
      count++;
    }
    if (dropped.length > 0) this.warn(`analytics: ${name}: dropped props ${dropped.join(", ")}`);
    return count > 0 ? out : undefined;
  }
}

function propValue(value: unknown): AnalyticsValue | undefined {
  switch (typeof value) {
    case "string":
      return truncate(value);
    case "number":
      return Number.isFinite(value) ? value : undefined;
    case "boolean":
      return value;
    case "object":
      return value === null ? null : undefined;
    default:
      return undefined;
  }
}

function truncate(value: string): string {
  if (value.length <= MAX_STRING) return value;
  const end = isHighSurrogate(value.charCodeAt(MAX_STRING - 1)) ? MAX_STRING - 1 : MAX_STRING;
  return value.slice(0, end);
}

function isHighSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff;
}

/** 32 hex chars. getRandomValues, unlike randomUUID, also works on plain-http dev hosts. */
function randomId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
